import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Box, ConditionalWrapper, Image, Link, Stack, Typography } from '@packages/shared';
import { useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';

export const headline = defineMessage({
  id: 'product.brandlogo-headline',
  defaultMessage: 'Art.-Nr.: {data}',
  description: 'The product ordernumber displayed next to the brand logo',
});

const HeadlineFragment = graphql(/* GraphQL */ `
  fragment HeadlineFragment on Product {
    brand {
      name
      image
    }
    name
    orderNumber
    promotion
    productSeo: seo(locale: $locale) {
      brandLink {
        url
      }
    }
  }
`);

/**
 * component to display a product headline
 *
 * for context-binding use `ProductHeadlineWithContext`
 */
export const ProductHeadline = ({
  productData,
}: {
  productData: FragmentType<typeof HeadlineFragment>;
}) => {
  const { brand, productSeo, name, orderNumber, promotion } = getFragmentData(
    HeadlineFragment,
    productData,
  );
  const { formatMessage } = useIntl();
  const { brandLink } = { ...productSeo };
  const [brandImage, setBrandImage] = useState(brand?.image ? brand.image : null);

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {brandImage && (
        <Box
          sx={{
            width: '52px',
            maxHeight: '52px',
          }}
        >
          <ConditionalWrapper
            condition={typeof brandLink?.url === 'string'}
            wrapper={(ch) => <Link href={brandLink!.url!}>{ch}</Link>}
          >
            <Image
              src={brandImage}
              alt={brand!.name}
              width={50}
              height={30}
              onError={() => setBrandImage(null)}
            />
          </ConditionalWrapper>
        </Box>
      )}
      <Box>
        <Typography variant="h5" component="h2">
          {name}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.darkTransparent' }}>
          {formatMessage(headline, { data: `${orderNumber}${promotion}` })}
        </Typography>
      </Box>
    </Stack>
  );
};
